/**
 * client/src/components/notifications/notification-row.tsx
 *
 * A single row inside the NotificationDrawer.
 *
 * Renders a `NotificationItem` from the aggregated feed rather than the
 * warehouse-backed `AlertItem` it previously took, so every actionable kind
 * (challenges, quorum, policy, admin grants, invites) can render here — not
 * only OSINT anomalies.
 *
 * DISMISS: shown ONLY for `dismissible` items (today: OSINT alerts, the one
 * kind with a real dismiss route). Every other kind clears by resolving its
 * underlying state, so rendering a dismiss control there would be a button that
 * silently does nothing.
 *
 * THE DISMISS TARGET MUST MATCH THE SOURCE TABLE. The feed reads OPERATIONAL
 * `osint_alerts`, so dismiss posts to `POST /api/osint/acknowledge/:id`
 * (`requireAuth`, scoped to the session user). It deliberately does NOT post to
 * `/api/anomaly/alerts/:id/dismiss`, whose `getAlertById`/`dismissAlert` run
 * against the WAREHOUSE `fact_anomaly_events` via `getAnalyticsDb()` — an
 * operational id does not exist there, so that route's org-admin resolver would
 * 404 and the button would fail silently forever.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { X } from "lucide-react";
import { postJSON } from "@/lib/queryClient";
import { formatRelativeTime, formatTimestamp } from "@/lib/format-timestamp";
import { NOTIFICATIONS_QUERY_KEY } from "@/lib/use-notifications";
import type { NotificationItem, NotificationSeverity } from "@shared/notification-types";
import { CREDENTIAL_ADDED_COPY } from "@shared/credential-append-copy";

/**
 * Severity → chip classes. `critical` is the ONLY red class and is reserved for
 * the Forensic-Pause / OSINT-critical case; `attention` uses amber; `info` uses
 * the neutral interactive token (CLAUDE.md §Design Constraints — red must never
 * be spent on ordinary user-actionable work).
 */
function severityChipClass(s: NotificationSeverity): string {
  if (s === "critical") return "bg-critical/10 text-critical border border-critical/30";
  if (s === "attention") return "bg-warning/10 text-warning border border-warning/30";
  return "bg-interactive/10 text-interactive border border-interactive/30";
}

function severityChipLabel(s: NotificationSeverity): string {
  if (s === "critical") return "Critical";
  if (s === "attention") return "Action needed";
  return "For info";
}

/**
 * Per-kind call-to-action text, rendered as a plain text line INSIDE the
 * row's one existing `<Link>` — never a second interactive control. Additive
 * only: a kind absent from this map renders exactly as it did before (no
 * CTA line at all). Today only `credential_append_notice` (Stage 7 "passkey
 * boundary gaps" module 01, AC-2) has one — the guarded (guardian-side) rows
 * from the same source ride this same generic rendering with no entry here,
 * per the controller addendum's "do not add a second CTA for it".
 */
const KIND_CTA_LABEL: Partial<Record<NotificationItem["kind"], string>> = {
  // #3169 Minor 8 — reused from the shared copy so the feed row's CTA and
  // the live WS toast's link label can never drift apart.
  credential_append_notice: CREDENTIAL_ADDED_COPY.linkLabel,
};

interface NotificationRowProps {
  readonly item: NotificationItem;
  readonly forensicPauseActive: boolean;
  readonly onNavigate?: () => void;
}

export function NotificationRow({
  item,
  forensicPauseActive,
  onNavigate,
}: NotificationRowProps): JSX.Element {
  const queryClient = useQueryClient();

  // `item.id` is source-prefixed (`anomaly:<uuid>`); the dismiss route wants the
  // bare row id.
  const rawId = item.id.slice(item.id.indexOf(":") + 1);

  const { mutate: dismiss, isPending, isError } = useMutation({
    mutationFn: () => postJSON(`/api/osint/acknowledge/${rawId}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      // The Security Centre and /alerts read their own keys; refresh them too
      // so a dismissed alert does not linger on whichever surface is mounted.
      void queryClient.invalidateQueries({ queryKey: ["osint/alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  return (
    <div
      className="flex items-start gap-2 px-3 py-2.5 hover:bg-bg-elevated transition-colors"
      data-testid="notification-row"
      data-kind={item.kind}
    >
      <Link
        href={item.href}
        onClick={onNavigate}
        className="flex-1 min-w-0 flex flex-col gap-1"
      >
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${severityChipClass(item.severity)}`}
            data-testid="severity-chip"
          >
            {severityChipLabel(item.severity)}
          </span>
        </div>
        <p className="text-xs text-text-primary line-clamp-2">{item.title}</p>
        {item.detail !== "" && (
          // M-1 (Task 3 fix round 1): the credential-append owner copy is
          // long enough that a single clamped line truncates it mid-sentence
          // — give this kind alone a second line. Every other kind keeps the
          // original single-line clamp.
          <p
            className={`text-[10px] text-text-secondary ${
              item.kind === "credential_append_notice" ? "line-clamp-2" : "line-clamp-1"
            }`}
          >
            {item.detail}
          </p>
        )}
        <span className="text-[10px] text-text-tertiary">
          {formatRelativeTime(new Date(item.timestamp))}
        </span>
        {item.deadline !== undefined && (
          // The wire carries ISO-8601 UTC and the copy carries no date literal
          // (Rule 13), so the deadline becomes readable here — in the viewer's
          // own locale and timezone, via the shared formatter.
          <span className="text-[10px] text-text-secondary" data-testid="notification-deadline">
            Until {formatTimestamp(new Date(item.deadline), { format: "compact_with_tz" })}
          </span>
        )}
        {KIND_CTA_LABEL[item.kind] !== undefined && (
          <span className="text-[10px] font-medium text-interactive">
            {KIND_CTA_LABEL[item.kind]}
          </span>
        )}
      </Link>

      {item.dismissible && (
        <button
          type="button"
          onClick={() => dismiss()}
          disabled={forensicPauseActive || isPending}
          // A failed dismiss must SAY so rather than looking like a no-op.
          aria-label={isError ? "Dismiss failed, try again" : "Dismiss alert"}
          title={isError ? "Could not dismiss — try again" : undefined}
          className={`shrink-0 mt-0.5 p-1 rounded hover:text-text-secondary disabled:opacity-40 transition-colors ${
            isError ? "text-warning" : "text-text-tertiary"
          }`}
          data-testid="dismiss-btn"
          data-dismiss-error={isError ? "true" : undefined}
        >
          <X size={12} aria-hidden />
        </button>
      )}
    </div>
  );
}
